'use client';

import type { FC, CSSProperties } from 'react';
import { Image } from '@/components/raven/Image';
import { getImageFocalPercentage } from '@/utils/image-utils';
import { useAppSettings } from '@/context/AppSettingsContext';

interface ArticleFeaturedImageProps {
	alt: string;
	aspectRatio?: `${number}:${number}`;
	className?: string;
	fill?: boolean;
	focalX?: number;
	focalY?: number;
	height?: number;
	priority?: boolean;
	sizes: string;
	src?: string;
	style?: CSSProperties;
	width?: number;
}

export const ArticleFeaturedImage: FC<ArticleFeaturedImageProps> = ({
	aspectRatio,
	className,
	focalX,
	focalY,
	height,
	src,
	width,
	...props
}) => {
	const { settings } = useAppSettings();
	const placeholder = settings.fallback_image;

	let style = {};

	// Checking for undefined values to avoid edge cases where one of the values is explicitly set to 0.
	if (
		typeof focalX === 'number' &&
		typeof focalY === 'number' &&
		typeof height === 'number' &&
		typeof width === 'number'
	) {
		const imageFocalPercentage = getImageFocalPercentage(
			focalX,
			focalY,
			height,
			width,
		);
		style = {
			objectPosition: `${imageFocalPercentage.x}% ${imageFocalPercentage.y}%`,
			transformOrigin: `${imageFocalPercentage.x}% ${imageFocalPercentage.y}%`,
		};
	}

	if (!src && !placeholder) {
		return null;
	}

	if (src) {
		return (
			<Image
				{...props}
				alt={props.alt ?? ''}
				aspectRatio={aspectRatio}
				className={className}
				src={src}
				style={{ ...style, ...props.style }}
			/>
		);
	}

	if (placeholder) {
		return (
			<Image
				alt=""
				aspectRatio={aspectRatio}
				className={className}
				fill
				src={placeholder}
				unoptimized
			/>
		);
	}

	return null;
};
